import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { btnPrimary } from '@/components/rm'
import { useT } from '@/i18n'
import { hasCompany } from '@/lib/account'

// Spec 002 US3. A client becomes a trade person. The server makes the company and writes the new
// claims; the session is refreshed so the next request carries them.
export const Route = createFileRoute('/_client/offer-services')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (hasCompany(data.session)) throw redirect({ to: '/dashboard' })
  },
  component: OfferServices,
})

function OfferServices() {
  const { t } = useT()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const businessName = name.trim()
    if (!businessName) return
    setBusy(true); setError('')
    const { error: err } = await supabase.functions.invoke('become-provider', { body: { businessName } })
    if (err) {
      setBusy(false)
      const ctx = (err as { context?: Response }).context
      let code = ''
      try { if (ctx) code = (await ctx.json())?.error ?? '' } catch { /* not JSON */ }
      return setError(code === 'already_provider' ? t('offer.already') : t('offer.failed'))
    }
    await supabase.auth.refreshSession()
    navigate({ to: '/dashboard' })
  }

  return (
    <div className="mx-auto max-w-md space-y-5">
      <div>
        <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('offer.title')}</h1>
        <p className="mt-1 text-[13.5px] text-token-secondary">{t('offer.subtitle')}</p>
      </div>
      <form onSubmit={submit} className="space-y-4 rounded-[16px] border border-token-subtle bg-token-surface p-5">
        <div className="space-y-1.5">
          <label htmlFor="biz" className="text-[13px] font-semibold text-token-secondary">{t('offer.label')}</label>
          <input id="biz" autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
            className="h-11 w-full rounded-[10px] border border-token-subtle px-3 text-[15px] text-token-primary outline-none focus:border-token-strong"
            placeholder={t('offer.placeholder')} />
        </div>
        <ul className="space-y-2 text-[13px] text-token-secondary">
          <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-token-success" />{t('offer.points')}</li>
          <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-token-success" />{t('offer.idNote')}</li>
        </ul>
        {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
        <button type="submit" disabled={busy || !name.trim()} className={`${btnPrimary} h-11 w-full justify-center`}>{busy ? t('offer.creating') : t('offer.create')}</button>
      </form>
    </div>
  )
}
